import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TasksService } from '@/services/tasks.service'

import { ITaskSearchMatch, ITaskSearchResponse } from '@/types/task.interface'
import { toastError } from '@/utils/toastError'

const KEYWORD_SEARCH_POLLING_INTERVAL = 30000

export const useKeywordSearch = (
	taskId?: string,
	isPollingEnabled: boolean = false
) => {
	const [keywords, setKeywords] = useState<string[]>([])
	const [searchResults, setSearchResults] = useState<ITaskSearchResponse[]>([])
	const keywordsRef = useRef<string[]>([])
	const isPollingRef = useRef(false)

	const normalizeKeyword = useCallback((keyword: string) => {
		return keyword.trim().toLowerCase()
	}, [])

	useEffect(() => {
		keywordsRef.current = keywords
	}, [keywords])

	const { mutateAsync: searchKeyword, isPending: isKeywordSearching } =
		useMutation({
			mutationKey: ['searchTaskKeywords', taskId],
			mutationFn: (keyword: string) => {
				if (!taskId) {
					return Promise.reject(new Error('Task id is required'))
				}

				return TasksService.searchTask({
					taskId,
					q: keyword
				})
			}
		})

	const updateSearchResults = useCallback(
		(responses: ITaskSearchResponse[]) => {
			setSearchResults(prev => {
				const currentKeywords = keywordsRef.current
				const currentKeywordKeys = currentKeywords.map(normalizeKeyword)
				const resultsMap = new Map(
					prev.map(result => [normalizeKeyword(result.query), result])
				)

				responses.forEach(response => {
					const responseKey = normalizeKeyword(response.query)

					if (currentKeywordKeys.includes(responseKey)) {
						resultsMap.set(responseKey, response)
					}
				})

				return currentKeywords
					.map(keyword => resultsMap.get(normalizeKeyword(keyword)))
					.filter(Boolean) as ITaskSearchResponse[]
			})
		},
		[normalizeKeyword]
	)

	const addKeyword = async (rawKeyword: string) => {
		const keyword = rawKeyword.trim()

		if (
			!keyword ||
			keywords.some(item => normalizeKeyword(item) === normalizeKeyword(keyword))
		) {
			return
		}

		setKeywords(prev => [...prev, keyword])

		try {
			const response = await searchKeyword(keyword)

			updateSearchResults([response])
		} catch (error) {
			console.log(error)
			setKeywords(prev => prev.filter(item => item !== keyword))
			toastError('Failed to search keyword')
		}
	}

	const removeKeyword = (keyword: string) => {
		setKeywords(prev => prev.filter(item => item !== keyword))
		setSearchResults(prev =>
			prev.filter(
				result => normalizeKeyword(result.query) !== normalizeKeyword(keyword)
			)
		)
	}

	const clearKeywords = () => {
		setKeywords([])
		setSearchResults([])
	}

	const keywordMatches = useMemo<ITaskSearchMatch[]>(() => {
		return searchResults.flatMap(result => result.matches)
	}, [searchResults])

	useEffect(() => {
		if (!taskId || !isPollingEnabled || !keywords.length) return

		const interval = setInterval(async () => {
			if (isPollingRef.current) return

			isPollingRef.current = true

			try {
				const results = await Promise.allSettled(
					keywordsRef.current.map(keyword => searchKeyword(keyword))
				)

				const fulfilledResponses = results
					.filter(
						(
							result
						): result is PromiseFulfilledResult<ITaskSearchResponse> =>
							result.status === 'fulfilled'
					)
					.map(result => result.value)

				updateSearchResults(fulfilledResponses)
			} catch (error) {
				console.log(error)
			} finally {
				isPollingRef.current = false
			}
		}, KEYWORD_SEARCH_POLLING_INTERVAL)

		return () => clearInterval(interval)
	}, [
		isPollingEnabled,
		keywords.length,
		searchKeyword,
		taskId,
		updateSearchResults
	])

	return {
		keywords,
		keywordMatches,
		isKeywordSearching,
		addKeyword,
		removeKeyword,
		clearKeywords
	}
}
